import * as d3 from "d3";
import { Award } from "lucide-react";
import { getPartyTextColor, getPartyColor } from "./Constants";
import { useRef, useEffect } from "react";

const abbreviateName = (name) => {
  const n = name.toLowerCase();
  if (n.includes('liberal') || n.includes('democrat')) return 'Lib Dem';
  if (n.includes('conservative')) return 'Con';
  if (n.includes('labour')) return 'Lab';
  if (n.includes('reform')) return 'Reform';
  if (n.includes('green')) return 'Green';
  if (n.includes('independent')) return 'Ind';
  return name.length > 8 ? name.substring(0, 6) + '..' : name;
};

// UPDATED: Now destructures 'colors' from props
export const ElectionResultsChart = ({ data, width = 500, height = 250, colors }) => {
  const svgRef = useRef();
  const tooltipRef = useRef();

  useEffect(() => {
    if (!data || !svgRef.current) return;
    
    // 1. Setup & Clear
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    // Calculate Total for Percentages
    const totalVotes = Object.values(data).reduce((acc, val) => acc + val, 0);

    const margin = { top: 25, right: 10, bottom: 25, left: 50 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);
    
    // 2. Process & Sort Data
    const parties = Object.entries(data)
      .filter(([, votes]) => votes > 0)
      .map(([party, votes]) => ({ 
          party, 
          votes,
          percent: totalVotes > 0 ? ((votes / totalVotes) * 100).toFixed(1) : 0 
      }))
      .sort((a, b) => b.votes - a.votes);

    // 3. Scales
    const x = d3.scaleBand()
      .domain(parties.map(d => d.party))
      .range([0, innerWidth])
      .padding(0.3);

    const y = d3.scaleLinear()
      .domain([0, d3.max(parties, d => d.votes) * 1.1])
      .range([innerHeight, 0]);

    // 4. Draw Bars
    g.selectAll("rect")
      .data(parties)
      .join("rect")
      .attr("x", d => x(d.party))
      .attr("y", d => y(d.votes))
      .attr("width", x.bandwidth())
      .attr("height", d => innerHeight - y(d.votes))
      // UPDATED: Robust Color Logic
      .attr("fill", d => {
          // A. Try the explicit colors prop first
          if (colors && colors[d.party]) return colors[d.party];
          if (colors && colors[d.party.toUpperCase()]) return colors[d.party.toUpperCase()];

          // B. Try the global helper
          const exactMatch = getPartyColor(d.party);
          if (exactMatch && exactMatch !== "#94a3b8") return exactMatch;

          // C. Fallback: Fuzzy Match keywords (Fixes 'Labour' vs 'LAB' mismatches)
          const name = d.party.toUpperCase();
          if (name.includes("LAB")) return getPartyColor("LAB") || "#e11d48";
          if (name.includes("CON")) return getPartyColor("CON") || "#3b82f6";
          if (name.includes("LIB") || name.includes("DEM")) return getPartyColor("LDM") || "#f59e0b";
          if (name.includes("GRN") || name.includes("GREEN")) return getPartyColor("GRN") || "#22c55e";
          if (name.includes("REF")) return getPartyColor("REF") || "#0ea5e9";
          
          return "#94a3b8"; // Default Slate
      })
      .attr("rx", 4)
      .on("mouseover", (event, d) => {
        const tooltip = d3.select(tooltipRef.current);
        tooltip.style("opacity", 1)
               .html(`
                 <div class="font-bold">${d.party}</div>
                 <div>${d.votes.toLocaleString()} votes</div>
                 <div class="text-slate-400 text-[10px] mt-0.5">${d.percent}% share</div>
               `);
      })
      .on("mousemove", (event) => {
        const tooltip = d3.select(tooltipRef.current);
        const [mouseX, mouseY] = d3.pointer(event, svgRef.current);
        tooltip.style("left", `${mouseX}px`)
               .style("top", `${mouseY - 10}px`)
               .style("transform", "translate(-50%, -100%)");
      })
      .on("mouseleave", () => {
        d3.select(tooltipRef.current).style("opacity", 0);
      });

    // 5. Add Labels
    g.selectAll(".label")
      .data(parties)
      .join("text")
      .attr("class", "label")
      .attr("x", d => x(d.party) + x.bandwidth() / 2)
      .attr("y", d => y(d.votes) - 5)
      .attr("text-anchor", "middle")
      .attr("fill", "#94a3b8")
      .style("font-size", "10px")
      .style("font-weight", "bold")
      .text(d => d.votes.toLocaleString());

    // 6. X Axis
    g.append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(d3.axisBottom(x).tickFormat(abbreviateName))
      .selectAll("text")
      .attr("transform", "translate(0, 5)")
      .style("text-anchor", "middle")
      .style("font-size", "10px")
      .style("font-family", "monospace")
      .style("fill", "#cbd5e1");

    g.selectAll(".domain").remove();
    g.selectAll("line").remove();

  }, [data, width, height, colors]);

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      <svg 
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`} 
        className="w-full h-full overflow-visible"
        style={{ minHeight: '200px' }}
      ></svg>
      <div 
        ref={tooltipRef} 
        className="absolute px-3 py-2 bg-slate-900/95 border border-slate-700 shadow-xl backdrop-blur-md text-white text-xs rounded pointer-events-none opacity-0 transition-opacity duration-150 z-50 whitespace-nowrap"
      ></div>
    </div>
  );
};

export const ElectionMetadata = ({ results }) => {
  if (!results) return null;
  const winner = results.winner || 'N/A';
  const winnerColor = getPartyTextColor(winner);
  return (
    <div className="flex flex-col justify-center space-y-6 p-4 h-full">
      <div className="flex items-center space-x-3">
        <Award size={24} className={winnerColor} />
        <div>
          <div className="text-sm text-slate-400">Winner</div>
          <div className={`text-xl font-bold ${winnerColor}`}>{winner}</div>
        </div>
      </div>
      <div>
        <div className="text-sm text-slate-400">Total Turnout</div>
        <div className="text-2xl font-bold text-white">{results.Turnout_number?.toLocaleString() || 'N/A'}</div>
      </div>
      <div>
        <div className="text-sm text-slate-400">Majority</div>
        <div className="text-2xl font-bold text-white">{results.Majority?.toLocaleString() || 0}</div>
      </div>
    </div>
  );
};